/**
 * @file ButtonComponent.tsx
 * @description
 * -----------------------------------------------------
 * Componente de botón
 * -----------------------------------------------------
 * @version 0.0.1a
 * @created 2024-03-03
 * @modified 2025-09-26
 * @author DLTCode
 * @author Nacho Moya
 * @copyright © DLTCode
 * @license Private
 * */
import { ButtonComponentProps } from "@/types/ui";
import { useTranslations } from "next-intl";
import clsx from "clsx";
import { useMemo } from "react";

const ButtonComponent = ({
  label,
  type = "button",
  onClick,
  children,
  className,
  onMouseEnter,
  onMouseLeave,
  disabled,
  loading,
  title,
  iconPosition = "left",
  variant = "solid",
  color = "primary",
  size = "md",
  width = "auto",
  icon: Icon,
  iconProps,
  ...props
}: ButtonComponentProps) => {
  const t = useTranslations("Buttons");
  const loadingT = useTranslations("Loading");

  const isDisabled = loading || disabled;

  const defaultIconProps = useMemo(
    () =>
      ({
        size: size === "sm" ? 16 : 20,
        // Iconos decorativos: marcar aria-hidden y omitir role="img" para
        // evitar el conflicto entre rol gráfico y elemento oculto a AT.
        "aria-hidden": true,
      }) as const,
    [size]
  );

  return (
    <button
      className={clsx(
        `btn`,
        `btn--${size}`,
        `btn--${color}${variant !== "solid" ? `--${variant}` : ""}`,
        width !== "auto" && `btn--${width}`,
        isDisabled && `btn--disabled`,
        className
      )}
      type={type}
      onClick={onClick}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      disabled={isDisabled}
      title={title}
      {...props}
    >
      {loading ? (
        <>{loadingT("loading")}</>
      ) : (
        <>
          {iconPosition === "left" && Icon && (
            <Icon {...defaultIconProps} {...iconProps} />
          )}
          {label && <span>{t(label)}</span>}
          {children}
          {iconPosition === "right" && Icon && (
            <Icon {...defaultIconProps} {...iconProps} />
          )}
        </>
      )}
    </button>
  );
};
export default ButtonComponent;
